import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectUserEmail } from "../store/slices/authUserSlice";
import debounce from "lodash.debounce";

import webhookService, { CustomerWebhook } from "../services/webhookService";
import { isWhiteLabel } from "../services/agencyService";
import personalAccessTokenService, { PersonalAccessToken } from "../services/personalAccessTokenService";
import landingPageService, { LandingPage } from "../services/landingPageService";

const sectionStyle: React.CSSProperties = {
  maxWidth: "600px",
  marginBottom: "40px",
};

const sectionHeaderStyle: React.CSSProperties = {
  marginBottom: "4px",
};

const sectionDescStyle: React.CSSProperties = {
  margin: "4px 0 20px 0",
  color: "#495057",
  fontSize: "14px",
};

const Settings: React.FC = () => {

  const userEmail = useSelector(selectUserEmail);
  const [webhook, setWebhook] = useState<CustomerWebhook | null>(null);
  const [webhookName, setWebhookName] = useState("");
  const [copiedToken, setCopiedToken] = useState(false);
  const [webhookError, setWebhookError] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);
  const [showUsage, setShowUsage] = useState(false);
  const [skipWelcomeEmail, setSkipWelcomeEmail] = useState(false);

  const [apiTokens, setApiTokens] = useState<PersonalAccessToken[]>([]);
  const [newTokenName, setNewTokenName] = useState("");
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [copiedApiToken, setCopiedApiToken] = useState(false);
  const [apiTokenError, setApiTokenError] = useState("");
  const [creating, setCreating] = useState(false);

  const [landingPages, setLandingPages] = useState<LandingPage[]>([]);

  useEffect(() => {
    personalAccessTokenService.list().then(setApiTokens).catch(() => {});
    landingPageService.list().then(setLandingPages).catch(() => {});
  }, []);

  const handleCreateToken = async () => {
    if (!newTokenName.trim()) return;
    setCreating(true);
    setApiTokenError("");
    try {
      const result = await personalAccessTokenService.create(newTokenName.trim());
      setCreatedToken(result.token || null);
      setNewTokenName("");
      const updated = await personalAccessTokenService.list();
      setApiTokens(updated);
    } catch {
      setApiTokenError("Failed to create API key. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeToken = async (id: number) => {
    try {
      await personalAccessTokenService.revoke(id);
      const updated = await personalAccessTokenService.list();
      setApiTokens(updated);
    } catch {
      setApiTokenError("Failed to revoke API key.");
    }
  };

  const copyApiToken = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedApiToken(true);
    setTimeout(() => setCopiedApiToken(false), 2000);
  };

  useEffect(() => {
    webhookService.getWebhook().then((data: CustomerWebhook) => {
      setWebhook(data);
      setWebhookName(data.name);
      setSkipWelcomeEmail(data.skip_welcome_email);
    }).catch(error => {
      console.error("Error fetching webhook:", error);
      setWebhookError(true);
    });
  }, []);

  const debouncedSave = useCallback(
    debounce((webhookId: number, name: string) => {
      webhookService.updateWebhookName(webhookId, name)
        .then(() => {
          setNameSaved(true);
          setTimeout(() => setNameSaved(false), 2000);
        })
        .catch(error => {
          console.error("Error saving webhook name:", error);
        });
    }, 500),
    []
  );

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setWebhookName(newValue);
    if (webhook) {
      debouncedSave(webhook.id, newValue);
    }
  };

  const handleSkipWelcomeEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setSkipWelcomeEmail(newValue);
    if (webhook) {
      webhookService.updateWebhookSkipWelcomeEmail(webhook.id, newValue).catch(error => {
        console.error("Error saving skip welcome email setting:", error);
        setSkipWelcomeEmail(!newValue);
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedToken(true);
    setTimeout(() => setCopiedToken(false), 2000);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }}>
      <div>
        <h1>Settings</h1>

        {/* Landing Pages */}
        {landingPages.length > 0 && (
          <div style={sectionStyle}>
            <h3 style={sectionHeaderStyle}>Landing Pages</h3>
            <p style={sectionDescStyle}>
              Your newsletter signup pages.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {landingPages.map((lp) => (
                <div key={lp.id} style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 16px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "6px",
                  border: "1px solid #dee2e6"
                }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: "14px", marginBottom: "2px" }}>
                      {lp.newsletter_name}
                    </div>
                    <a
                      href={lp.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: "13px", color: "#007bff" }}
                    >
                      {lp.url}
                    </a>
                  </div>
                  <a
                    href={lp.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: "13px",
                      color: "#007bff",
                      fontWeight: 500,
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                      marginLeft: "16px"
                    }}
                  >
                    Visit &rarr;
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Email */}
        <div style={sectionStyle}>
          <h3 style={sectionHeaderStyle}>Primary Email</h3>
          <p style={{ ...sectionDescStyle, marginBottom: "0" }}>{userEmail}</p>
        </div>

        {/* Cancel Subscription */}
        <div style={sectionStyle}>
          <h3 style={sectionHeaderStyle}>Subscription</h3>
          <p style={sectionDescStyle}>
            You can cancel your subscription by{" "}
            <a href="https://billing.stripe.com/p/login/5kA7tpbg6cSJ8gwbII">
              logging into your Stripe account here
            </a>.
          </p>
        </div>

        {/* Webhook Settings */}
        <div style={sectionStyle}>
          <h3 style={sectionHeaderStyle}>Webhook Settings</h3>
          {webhookError ? (
            <div style={{
              padding: "16px",
              backgroundColor: "#fff3cd",
              color: "#856404",
              borderRadius: "4px",
              fontSize: "14px",
              marginTop: "12px"
            }}>
              Unable to load webhook settings. Please contact support if this continues.
            </div>
          ) : webhook ? (
            <>
              <p style={sectionDescStyle}>
                Use the following token and URL to connect your forms, Zapier, or other tools to automatically add subscribers.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              <div>
                <label htmlFor="webhook_name" style={{ display: "block", marginBottom: "4px", fontWeight: "500" }}>Name:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    id="webhook_name"
                    type="text"
                    value={webhookName}
                    onChange={handleNameChange}
                    placeholder="My Webhook"
                    className="url-input"
                    style={{ flex: 1 }}
                  />
                  {nameSaved && (
                    <span style={{
                      color: "#28a745",
                      fontSize: "14px",
                      fontWeight: "500",
                      whiteSpace: "nowrap"
                    }}>
                      Saved ✓
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label
                  title="Subscribers added through this webhook will not receive the welcome journey or welcome email configured for this list."
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                    cursor: "pointer",
                    width: "fit-content",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={skipWelcomeEmail}
                    onChange={handleSkipWelcomeEmailChange}
                    style={{ cursor: "pointer" }}
                  />
                  Skip welcome email for subscribers added via this webhook ⓘ
                </label>
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>LinkedIn Extension Token:</label>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 12px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "4px",
                  border: "1px solid #dee2e6"
                }}>
                  <div style={{
                    fontFamily: "monospace",
                    fontSize: "13px",
                    color: "#495057",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: "0 1 auto",
                    minWidth: "0"
                  }}>
                    {showToken ? webhook.token : "po_sk_••••••••••••••••••••••••"}
                  </div>
                  <button
                    onClick={() => copyToClipboard(webhook.token)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      color: copiedToken ? "#28a745" : "#6c757d",
                      fontSize: "16px",
                      flexShrink: 0
                    }}
                    title="Copy to clipboard"
                  >
                    {copiedToken ? "✓" : "📋"}
                  </button>
                  <button
                    onClick={() => setShowToken(!showToken)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#007bff",
                      fontSize: "13px",
                      fontWeight: "500",
                      padding: "0",
                      flexShrink: 0
                    }}
                  >
                    {showToken ? "Hide" : "Show"}
                  </button>
                </div>
                {webhook.revoked_at ? (
                  <div style={{
                    padding: "12px",
                    backgroundColor: "#ffebee",
                    color: "#c62828",
                    borderRadius: "4px",
                    fontSize: "14px",
                    marginTop: "8px"
                  }}>
                    ⚠️ This token has been revoked and is inactive. Please contact support.
                  </div>
                ) : (
                  <div style={{ fontSize: "13px", color: "#666", marginTop: "8px" }}>
                    {webhook.last_used_at
                      ? `Last used: ${new Date(webhook.last_used_at).toLocaleString()}`
                      : "Not used yet"}
                  </div>
                )}
              </div>

              {!isWhiteLabel() && (
                <div>
                  <label style={{ display: "block", marginBottom: "4px", fontWeight: "500" }}>Webhook URL:</label>
                  <code style={{
                    display: "block",
                    padding: "10px 12px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "4px",
                    fontFamily: "monospace",
                    fontSize: "13px",
                    color: "#495057",
                    border: "1px solid #dee2e6"
                  }}>
                    https://api.withpotions.com/v1/subscribers
                  </code>
                  <div style={{ marginTop: "16px" }}>
                    <div
                      onClick={() => setShowUsage(!showUsage)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        marginBottom: "8px"
                      }}
                    >
                      <span style={{
                        fontSize: "0.8em",
                        color: "#666",
                        transition: "transform 0.2s",
                        transform: showUsage ? "rotate(90deg)" : "rotate(0deg)"
                      }}>
                        ▶
                      </span>
                      <span style={{
                        color: "#007bff",
                        fontSize: "14px",
                        fontWeight: "500"
                      }}>
                        Usage
                      </span>
                    </div>
                    {showUsage && (
                      <pre style={{
                        marginTop: "8px",
                        padding: "12px",
                        backgroundColor: "#1e1e1e",
                        color: "#d4d4d4",
                        borderRadius: "4px",
                        overflow: "auto",
                        fontSize: "12px"
                      }}>
{`curl -X POST https://api.withpotions.com/v1/subscribers \\
  -H "Authorization: ${webhook.token}" \\
  -H "Content-Type: application/json" \\
  -d '{"email": "user@example.com"}'`}
                      </pre>
                    )}
                  </div>
                </div>
              )}
            </div>
            </>
          ) : (
            <div style={{ fontSize: "14px", color: "#666" }}>
              Loading webhook settings...
            </div>
          )}
        </div>

        {/* API Keys */}
        <div style={sectionStyle}>
          <h3 style={sectionHeaderStyle}>API Keys</h3>
          <p style={sectionDescStyle}>
            Create API keys to authenticate MCP tools (e.g. Claude Code) or other integrations.
          </p>

          {createdToken && (
            <div style={{
              padding: "16px",
              backgroundColor: "#e0f7df",
              color: "#2e7d32",
              borderRadius: "4px",
              fontSize: "14px",
              marginBottom: "16px"
            }}>
              <strong>Copy your API key now — it won't be shown again.</strong>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginTop: "8px",
                padding: "8px 12px",
                backgroundColor: "#fff",
                borderRadius: "4px",
                border: "1px solid #c8e6c9"
              }}>
                <code style={{
                  fontFamily: "monospace",
                  fontSize: "13px",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "#333"
                }}>
                  {createdToken}
                </code>
                <button
                  onClick={() => copyApiToken(createdToken)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    color: copiedApiToken ? "#28a745" : "#6c757d",
                    fontSize: "16px",
                    flexShrink: 0
                  }}
                  title="Copy to clipboard"
                >
                  {copiedApiToken ? "Copied!" : "Copy"}
                </button>
              </div>
              <button
                onClick={() => setCreatedToken(null)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#2e7d32",
                  fontSize: "12px",
                  marginTop: "8px",
                  padding: 0,
                  textDecoration: "underline"
                }}
              >
                Dismiss
              </button>
            </div>
          )}

          {apiTokenError && (
            <div style={{
              padding: "12px",
              backgroundColor: "#ffebee",
              color: "#d32f2f",
              borderRadius: "4px",
              fontSize: "14px",
              marginBottom: "16px"
            }}>
              {apiTokenError}
            </div>
          )}

          <div style={{ display: "flex", gap: "8px", marginBottom: "24px" }}>
            <input
              type="text"
              value={newTokenName}
              onChange={(e) => setNewTokenName(e.target.value)}
              placeholder='Name (e.g. "Claude Code")'
              className="url-input"
              style={{ flex: 1 }}
              onKeyDown={(e) => e.key === "Enter" && handleCreateToken()}
            />
            <button
              onClick={handleCreateToken}
              disabled={creating || !newTokenName.trim()}
              className="primary-button"
              style={{ whiteSpace: "nowrap", width: "auto", padding: "0 20px" }}
            >
              {creating ? "Creating..." : "Create Key"}
            </button>
          </div>

          {apiTokens.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #dee2e6", textAlign: "left" }}>
                  <th style={{ padding: "8px 12px" }}>Name</th>
                  <th style={{ padding: "8px 12px" }}>User</th>
                  <th style={{ padding: "8px 12px" }}>Created</th>
                  <th style={{ padding: "8px 12px" }}>Last Used</th>
                  <th style={{ padding: "8px 12px" }}></th>
                </tr>
              </thead>
              <tbody>
                {apiTokens.map((t) => (
                  <tr key={t.id} style={{
                    borderBottom: "1px solid #dee2e6",
                    opacity: t.revoked ? 0.5 : 1
                  }}>
                    <td style={{ padding: "8px 12px" }}>
                      {t.name}
                      {t.multi_tenant && (
                        <span style={{
                          marginLeft: "8px",
                          background: "#e3f2fd",
                          color: "#1565c0",
                          fontSize: "11px",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontWeight: 600
                        }}>Multi-tenant</span>
                      )}
                    </td>
                    <td style={{ padding: "8px 12px", color: "#495057" }}>{t.user_email}</td>
                    <td style={{ padding: "8px 12px" }}>
                      {new Date(t.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: "8px 12px", color: "#666" }}>
                      {t.last_used_at
                        ? new Date(t.last_used_at).toLocaleDateString()
                        : "Never"}
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      {t.revoked ? (
                        <span style={{ color: "#c62828", fontSize: "13px" }}>Revoked</span>
                      ) : (
                        <button
                          onClick={() => handleRevokeToken(t.id)}
                          style={{
                            background: "none",
                            border: "1px solid #dc3545",
                            borderRadius: "4px",
                            color: "#dc3545",
                            cursor: "pointer",
                            padding: "4px 12px",
                            fontSize: "13px"
                          }}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
